import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  generatePath,
  Link,
  matchPath,
  useLocation
  } from 'react-router-dom';
import AnimateHeight from 'react-animate-height';
import { useAmplitude } from 'react-amplitude-hooks';
import SidebarLink from './SidebarLink';
import { getTranslation } from 'Helpers/translations';
import { isTenantFeatureAvailable } from 'Helpers/toggleFeatures';
import infoNotification from 'Helpers/infoNotification';
import { getAccountRedirectPath } from 'Hooks/usePostLoginRedirect';
import { Arrow } from 'Components/shared/symbols';
import Text from "Components/shared/Text";
import { AuthContext } from 'States/auth/authState';
import { ConfigContext } from 'States/config/configState';
import { UiContext } from 'States/ui/uiState';
import { KycContext } from 'States/kyc/kycState';
import { LangContext } from 'States/lang/langState';
import { i18nextKeys } from 'Lang/i18nextKeys';
import i18nextTranslate from 'Lang/i18nextTranslate';
import { AndroidIcon, IOSIcon } from 'Components/shared/symbols'
import LanguageSwitcher from 'Components/Sidebar/LanguageSwitcher';
import { STORAGE_KEYS, TENANT_FEATURE } from 'Enums';
import { KYC_PROVIDER } from 'Enums/KycProvider';
import useConfigSettings from 'Hooks/useConfigSettings';
import useKycTierCheck from 'Hooks/useKycTierCheck';
import { ROUTES } from 'Router/Routes';
import BurgerMenu from 'Components/shared/BurgerMenu';
import SidebarLogo from './SidebarLogo';

const isActiveRoute = (pathName, route, exact = false) =>
  matchPath(pathName, { path: route, exact });

const Sidebar = ({
  features,
  restrictedMode,
  featuredAsset = null
}) => {
  const [accountRoute, setAccountRoute] = useState(ROUTES.account);
  const [showKycReminder, setShowKycReminder] = useState(false);
  const [showActivationToast, setShowActivationToast] = useState(false);
  const [isMenuExpanded, setMenuExpanded] = useState(false);
  const {
    isActive,
    isAdmin,
    isAuthenticated,
    isBlocked,
    isEmailConfirmed,
    user,
    status,
    getStatus,
    checkEmailConfirmation
  } = useContext(AuthContext);
  const {
    breakpoints: { md }
  } = useContext(UiContext);
  const {
    config: {
      checkoutSettings,
      kycTiersDescription,
      kycTierFunctionalRestrictions,
      androidUrl,
      iosUrl
    }
  } = useContext(ConfigContext);
  const {
    isKycEnabled,
    getUserKycData,
    isKycCaseLoading
  } = useContext(KycContext);
  const { lang } = useContext(LangContext);
  const { pathname } = useLocation();
  const { logEvent } = useAmplitude();

  const {
    data: {
      defaultLanguageCode,
      supportedLanguagesCount
    }
  } = useConfigSettings.query({
    select: useCallback(({ Languages }) => {
      const defaultLanguage = Languages.find(({ IsDefault }) => IsDefault)
      return {
        defaultLanguageCode: defaultLanguage?.Code,
        supportedLanguagesCount: Languages.length
      }
    }, [])
  });

  const { isUserTierSufficient: purchaseVisibilityReached } = useKycTierCheck(
    kycTierFunctionalRestrictions?.visibility?.purchase
  );

  const isFeaturedAssetEnabled = !!featuredAsset &&
    (!isAuthenticated || (isAdmin || purchaseVisibilityReached));

  const featuredMenuText = useMemo(() => {
    if (isFeaturedAssetEnabled && lang && defaultLanguageCode) {
      const translations = getTranslation("CustomPurchaseSettings", lang, defaultLanguageCode, featuredAsset.Translations);
      return translations.SidebarLink;
    } else {
      return "";
    }
  }, [isFeaturedAssetEnabled, lang, defaultLanguageCode])

  // const showAccountReminder = isAuthenticated && !isActive && !isBlocked &&
  //   !isActiveRoute(
  //     pathname,
  //     [ROUTES.signup, ROUTES.confirmEmail, ROUTES.verifyProfile],
  //     true
  //   );

  useEffect(() => {
    const loadLatestKycCase = async () => {
      const response = await getUserKycData();
      if (!response?.LatestCase) {
        setShowKycReminder(true);
      }
    }
    if (isActive && !isAdmin && isKycEnabled && !isKycCaseLoading) {
      loadLatestKycCase();
    } else {
      setShowKycReminder(false);
    }
  }, [isKycEnabled, isActive, isAdmin, pathname]);

  useEffect(() => {
    if (!isAuthenticated || isActiveRoute(pathname, ROUTES.logout, true)) {
      return;
    }
    getStatus(true);
    checkEmailConfirmation(true);
  }, [isAuthenticated, pathname]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const route = getAccountRedirectPath(status, isEmailConfirmed) || ROUTES.account;
    if (accountRoute !== route) {
      setAccountRoute(route);
    }
  }, [isAuthenticated, status, isEmailConfirmed]);

  useEffect(() => {
    if (!isActive && !isBlocked) {
      setShowActivationToast(true);
    }
  }, [isActive, isBlocked]);

  useEffect(() => {
    if (isActive && showActivationToast) {
      infoNotification(
        i18nextTranslate(i18nextKeys.signupNotificationSuccess),
        "accountActivation"
      );
      setShowActivationToast(false);
    }
  }, [isActive, showActivationToast]);

  const toggleMenu = () => setMenuExpanded(!isMenuExpanded);

  const trackLogout = () => logEvent('Logout', {
    screen: location.pathname.split("/")[1] || "home"
  });

  const links = [
    {
      route: !isAdmin && restrictedMode && featuredAsset
        ? generatePath(
            ROUTES.featuredAsset,
            { id: featuredAsset.Id }
          )
        : ROUTES.featuredAssetBase,
      text: featuredMenuText,
      requiresAuth: isAuthenticated,
      enabled: isFeaturedAssetEnabled,
      dataQa: 'sidebar-customPurchase'
    },
    {
      route: generatePath(ROUTES.shop),
      text: i18nextTranslate(i18nextKeys.sidebarShop),
      requiresAuth: isAuthenticated,
      enabled:
        (isAdmin || (!restrictedMode && !checkoutSettings.hideShop)) &&
        isTenantFeatureAvailable(features, TENANT_FEATURE.purchase) &&
        purchaseVisibilityReached,
      dataQa: 'sidebar-purchase',
    },
    ...i18nextTranslate(i18nextKeys.sidebarLinks, { returnObjects: true }).map((link, index) => ({
      text: link.text,
      route: link.path,
      href: link.url,
      newTab: link.newTab,
      enabled: link.enabled,
      external: link.external,
      requiresAuth: isAuthenticated,
      dataQa: `sidebar-arbitrary-link-${index + 1}`
    })),
    {
      route: ROUTES.admin.kyc,
      text: i18nextTranslate(i18nextKeys.sidebarKycCases),
      requiresAuth: true,
      adminOnly: true,
      enabled: isTenantFeatureAvailable(features, TENANT_FEATURE.kyc),
      dataQa: 'sidebar-admin-kyc'
    },
    {
      route: ROUTES.admin.orders,
      text: i18nextTranslate(i18nextKeys.sidebarOrderManagement),
      requiresAuth: true,
      adminOnly: true,
      enabled: true,
      dataQa: 'sidebar-admin-purchase'
    },
    {
      route: ROUTES.admin.redemptions,
      text: i18nextTranslate(i18nextKeys.sidebarRedemptionManagement),
      requiresAuth: true,
      adminOnly: true,
      enabled: isTenantFeatureAvailable(features, TENANT_FEATURE.redeem),
      dataQa: 'sidebar-admin-redeem'
    },
    {
      route: ROUTES.admin.config.tenant,
      text: i18nextTranslate(i18nextKeys.sidebarConfigManagement),
      requiresAuth: true,
      adminOnly: true,
      enabled: true,
      dataQa: 'sidebar-admin-config'
    },
    {
      route: ROUTES.admin.config.json,
      text: i18nextTranslate(i18nextKeys.sidebarJsonConfigManagement),
      requiresAuth: true,
      adminOnly: true,
      enabled: true,
      dataQa: 'sidebar-admin-json-config'
    },
    {
      route: ROUTES.login,
      text: i18nextTranslate(i18nextKeys.customSidebarLogin),
      requiresAuth: false,
      enabled: true,
      dataQa: 'sidebar-login',
      state: {
        [STORAGE_KEYS.authReturnPath]: pathname
      },
      onClick: () => logEvent(
        "Auth login started",
        { origin: "menu" }
      )
    },
    {
      route: ROUTES.logout,
      text: i18nextTranslate(i18nextKeys.buttonLogout),
      requiresAuth: true,
      enabled: true,
      dataQa: 'sidebar-logout',
      onClick: trackLogout
    }
  ];

  return (
    <div
      data-qa="sidebar"
      className="flex flex-col sticky md:fixed top-0 md:top-auto left-0 right-0 md:right-auto text-xl md:text-xs z-50 menu-animate bg-white"
      style={{
        minWidth: "85px"
      }}
      onClick={toggleMenu}
    >
      <div className="md:h-screen overflow-auto flex flex-col md:px-20 md:py-10">
        <div className="w-full md:w-auto flex justify-between items-center px-10 md:px-0 py-10 md:mb-20 bg-white">
          <SidebarLogo />
          {supportedLanguagesCount > 1 && <LanguageSwitcher />}
          {!md ? (
            <BurgerMenu
              onClick={toggleMenu}
            />
          ) : null}
        </div>
        <AnimateHeight
          duration={750}
          height={md || isMenuExpanded ? 'auto' : 0}
          contentClassName="flex flex-col flex-grow justify-between "
          className="absolute md:static w-full md:w-auto flex flex-col flex-grow bg-white"
          style={{ ...!md && { top: "86px" } }}
        >
          <nav className="flex flex-col mb-104 md:mb-auto mx-10 mt-10 md:mx-0 md:mt-0 bg-white">
            {isAdmin && (
              <div
                className={`flex-none flex items-center justify-between pl-10 pr-20 py-8 color-7 leading-5 rounded-l-lg ${
                  // isActiveRoute(pathname, 'account')
                  false
                    ? 'sidebarLink-active-highlight'
                    : ''
                } menu-item-text`}
                data-qa="sidebar-account"
              >
                <Link
                  to={accountRoute}
                  data-qa="sidebar-account-link"
                  className="w-5/6 overflow-hidden"
                >
                  <div className="flex flex-wrap">
                    <Text textStyle="p2" color="color-7">
                      {`${user.profile.given_name} ${user.profile.family_name}`}
                    </Text>
                  </div>
                  <div
                    data-qa="sidebar-account-email"
                  >
                    <Text textStyle="p4" color="color-7">
                      {user.profile.email}
                    </Text>
                  </div>
                </Link>
              </div>
            )}
            {showKycReminder && (
              <Link
                to={ROUTES.kyc}
                target="_blank"
                onClick={() => logEvent("KYC started", {
                  origin: "reminder",
                  provider: kycTiersDescription[1]?.providers[0].name !== KYC_PROVIDER.manual
                })}
                className="kyc-reminder flex flex-no-wrap justify-between items-center gap-12 py-12 px-16 m-16 rounded-lg normal-case text-xs color-white bg-4--20"
                data-qa="sidebar-kycReminder"
              >
                <span className="w-5/6 multiline-truncate-3">
                  {i18nextTranslate(i18nextKeys.sidebarKycReminder)}
                </span>
                <Arrow size="20" />
              </Link>
            )}
            {/* {showAccountReminder && ( */}
            {false && (
              <Link
                to={accountRoute}
                className="flex items-center gap-12 py-12 px-16 mx-16 my-12 rounded-lg bg-4--20"
                data-qa="sidebar-accountReminder"
              >
                <Text
                  textStyle="text-small"
                  color="color-white"
                >
                  {i18nextTranslate(i18nextKeys.signupNotificationSidebar)}
                </Text>
              </Link>
            )}
            <ul className="subpixel-antialiased color-7 uppercase w-full self-end">
              {links.map((link, index) => (
                  <SidebarLink
                    key={`sidebar-link-${index}`}
                    link={{
                      ...link,
                      onClick: () => {
                        link.onClick && link.onClick();
                        toggleMenu();
                      }
                    }}
                  />
              ))}
            </ul>
          </nav>
          <div className="mt-auto flex flex-col m-10 md:m-0 bg-white">
            {androidUrl ? (
              <div className='flex items-center menu-item'>
                <div className='p-10'>
                  <AndroidIcon size="30px" />
                </div>
                <div className='menu-item-animate'>
                  <a
                    href={androidUrl}
                    className='py-10 pl-10 pr-20 menu-item-text'
                    style={{
                      fontFamily: "Verdana, Geneva, sans-serif",
                      fontSize: "16px",
                      lineHeight: "16px",
                      color: "#4c4c4c"
                    }}
                  >
                    {i18nextTranslate(i18nextKeys.sidebarAndroid)}
                  </a>
                </div>
              </div>
            ) : null}
            {iosUrl ? (
              <div className='flex items-center'>
                <div className='p-10'>
                  <IOSIcon size="30px" />
                </div>
                <div className='menu-item-animate'>
                  <a
                    href={iosUrl}
                    className='py-10 pl-10 pr-20 menu-item-text'
                    style={{
                      fontFamily: "Verdana, Geneva, sans-serif",
                      fontSize: "16px",
                      lineHeight: "16px",
                      color: "#4c4c4c"
                    }}
                  >
                    {i18nextTranslate(i18nextKeys.sidebarIOS)}
                  </a>
                </div>
              </div>
            ) : null}
            {isAdmin && (
              <Link
                to={ROUTES.about}
                data-qa="sidebar-about"
                className="menu-item-animate"
              >
                <Text
                  textStyle="h3"
                  color="color-7"
                  className='menu-item-text singleline-truncate whitespace-no-wrap'
                >
                  {`${i18nextTranslate(i18nextKeys.about)} (${
                    i18nextTranslate(i18nextKeys.commonVersion)
                  } ${process.env.REACT_APP_VERSION.split("-")[0]})`}
                </Text>
              </Link>
            )}
          </div>
          {!md && isMenuExpanded && (
            <div
              className="flex-grow"
              onClick={toggleMenu}
            ></div>
          )}
        </AnimateHeight>
      </div>
      {/* {!md && isMenuExpanded && (
        <div
          onClick={toggleMenu}
          className="absolute w-full h-full"
        ></div>
      )} */}
    </div>
  );
};

export default Sidebar;
